package components

import androidx.compose.runtime.Composable
import frontend.lib.classList
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.Element
import org.w3c.dom.HTMLDListElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement


@Composable
fun XIcon(attrs: AttrBuilderContext<*> = {}) {
    SVGBase(attrs) {
        PathBase("M6 18L18 6M6 6l12 12")
    }
}

@Composable
fun MenuIcon(attrs: AttrBuilderContext<*> = {}) {
    SVGBase(attrs) {
        PathBase("M4 6h16M4 12h16M4 18h16")
    }
}

@Composable
fun UserIcon() {
    SVGBase() {
        PathBase("M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z")
    }
}

@Composable
fun BellIcon() {
    SVGBase() {
        PathBase("M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9")
    }
}

@Composable
fun TrashIcon(classString: String = "h-6 w-6") {
    SVGBase(attrs = { classList(classString) }) {
        PathBase("M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16")
    }
}

@Composable
fun RefreshIcon(classString: String = "h-6 w-6") {
    SVGBase(attrs = { classList(classString) }) {
        PathBase("M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15")
    }
}

@Composable
fun InformationCircle(classString: String = "h-6 w-6") {
    SVGBase(attrs = { classList(classString) }) {
        PathBase("M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z")
    }
}

@Composable
fun ArrowDownSm(classString: String = "h-6 w-6") {
    SVGBase(attrs = { classList(classString) }) {
        PathBase("M17 13l-5 5m0 0l-5-5m5 5V6")
    }
}

@Composable
fun ArrowUpSm(classString: String = "h-6 w-6") {
    SVGBase(attrs = { classList(classString) }) {
        PathBase("M7 11l5-5m0 0l5 5m-5-5v12")
    }
}

@Composable
fun ClipboardCopyIcon(classString: String = "h-6 w-6") {
    SVGBase(attrs = { classList(classString) }) {
        PathBase("M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3")
    }
}

class SVGBuilderImplementation<TElement : Element>(private val tagName: String) : ElementBuilder<TElement> {
    private val el: Element by lazy { document.createElementNS("http://www.w3.org/2000/svg", tagName) }
    override fun create(): TElement = el.cloneNode() as TElement
}

val Svg: ElementBuilder<HTMLElement> = SVGBuilderImplementation("svg")
val Path: ElementBuilder<HTMLElement> = SVGBuilderImplementation("path")
val Circle: ElementBuilder<HTMLElement> = SVGBuilderImplementation("circle")

@Composable
fun Svg(
    attrs: AttrBuilderContext<HTMLElement>? = null,
    content: ContentBuilder<HTMLElement>? = null
) {
    TagElement(
        elementBuilder = Svg,
        applyAttrs = attrs,
        content = content
    )
}

@Composable
fun Path(
    attrs: AttrBuilderContext<HTMLElement>? = null,
    content: ContentBuilder<HTMLElement>? = null
) {
    TagElement(
        elementBuilder = Path,
        applyAttrs = attrs,
        content = content
    )
}

@Composable
fun Circle(
    attrs: AttrBuilderContext<HTMLElement>? = null,
    content: ContentBuilder<HTMLElement>? = null
) {
    TagElement(
        elementBuilder = Circle,
        applyAttrs = attrs,
        content = content
    )
}

@Composable
fun PathBase(d: String) {
    Path(attrs = {
        attr("stroke-linecap", "round")
        attr("stroke-linejoin", "round")
        attr("stroke-width", "2")
        attr("d", d)
    })
}

@Composable
fun SVGBase(attrs: AttrBuilderContext<*> = {}, content: @Composable () -> Unit = {}) {
    Svg(attrs = {
        classList("h-6 w-6")
        attr("xmlns", "http://www.w3.org/2000/svg")
        attr("fill", "none")
        attr("viewBox", "0 0 24 24")
        attr("stroke", "currentColor")
        attr("aria-hidden", "true")
        attrs()
    }) { content() }
}

@Composable
fun Loader(attrs: AttrBuilderContext<*> = {}) {
    Svg(attrs = {
        classList("animate-spin -ml-1 mr-3 h-5 w-5 text-white")
        attr("xmlns", "http://www.w3.org/2000/svg")
        attr("fill", "none")
        attr("viewBox", "0 0 24 24")
        attrs()
    }) {
        Circle(attrs = {
            classList("opacity-25")
            attr("cx", "12")
            attr("cy", "12")
            attr("r", "10")
            attr("stroke", "currentColor")
            attr("stroke-width", "4")
        })
        Path(attrs = {
            classList("opacity-75")
            attr("fill", "currentColor")
            attr("d", "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z")
        })
    }

//    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
//    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//    </svg>
}